import { isPlayerStatus, SPELLS, type SpellDefinition, type StatusId } from '@shefira/shared';

import { uiFormat, uiText } from './text.js';

/**
 * A row of timed states (ADR 0048): the player's own over the spell bar, a
 * creature's in the target frame. Each shows an icon, the seconds left, a
 * shadow that fills as it wears off, and what it does in a bubble on hover
 * (a tap on a touch screen). Not a `title`: the browser never shows one over
 * an element repainted every frame, and the sweep is.
 *
 * Display only: the server says a state landed and for how long (`status`);
 * the strip counts it down on its own clock, as the marks over the head do.
 */

interface Shown {
  element: HTMLElement;
  sweep: HTMLElement;
  left: HTMLElement;
  endsAt: number;
  durationMs: number;
  /** Name and effect, the first lines of the bubble; the time left follows. */
  text: string;
}

/** Keeps « Encore 7 s. » on one line of the bubble. */
const NBSP = String.fromCharCode(160);

/**
 * The spell whose icon stands for a creature's state: several spells stun,
 * the one that does nothing else says it best.
 */
const MOB_STATUS_SPELL: Partial<Record<StatusId, string>> = {
  stunned: 'shield-bash',
  slowed: 'seismic-wave',
  bleeding: 'rend',
  taunted: 'taunt',
};

/** A creature's states are named for what it suffers, not for the spell. */
const MOB_STATUS_NAME: Partial<Record<StatusId, string>> = {
  stunned: 'Étourdi',
  slowed: 'Ralenti',
  bleeding: 'Saignement',
  taunted: 'Provoqué',
};

/** The spell that lays a state, for its icon, its name and its power. */
function spellFor(status: StatusId): SpellDefinition | undefined {
  const named = MOB_STATUS_SPELL[status];
  if (named !== undefined) return SPELLS[named];
  return Object.values(SPELLS).find((spell) =>
    spell.effects.some((effect) => effect.kind === 'status' && effect.status === status),
  );
}

function powerOf(spell: SpellDefinition | undefined, status: StatusId): number {
  const effect = spell?.effects.find(
    (candidate) => candidate.kind === 'status' && candidate.status === status,
  );
  return effect?.kind === 'status' ? effect.power : 0;
}

/** What a state does, in the player's words. */
function effectText(status: StatusId, power: number): string {
  const share = `${String(Math.round(power * 100))} %`;
  switch (status) {
    case 'guarded':
      return uiFormat('Dégâts reçus réduits de {share}.', { share });
    case 'enraged':
      return uiFormat('DPS augmenté de {share}.', { share });
    case 'stunned':
      return uiText('Ne bouge ni ne frappe.');
    case 'slowed':
      return uiText(power === 2 ? 'Avance deux fois moins vite.' : 'Avance moins vite.');
    case 'bleeding':
      return uiText('Perd de la vie chaque seconde.');
    case 'taunted':
      return uiText('Ne s’en prend qu’à celui qui l’a provoqué.');
  }
}

function describe(status: StatusId, spell: SpellDefinition | undefined): string {
  const name = isPlayerStatus(status)
    ? (spell?.name ?? status)
    : uiText(MOB_STATUS_NAME[status] ?? status);
  return uiFormat('{name} : {effect}', {
    name,
    effect: effectText(status, powerOf(spell, status)),
  });
}

export interface EffectStripOptions {
  /** The bubble opens under the icons (a strip at the top of the screen) rather than over them. */
  tipBelow?: boolean;
}

export class EffectStrip {
  readonly root: HTMLElement;
  private readonly shown = new Map<StatusId, Shown>();
  private readonly tip: HTMLElement;
  /** The state whose bubble is open, if one is. */
  private hovered: StatusId | null = null;

  constructor(options: EffectStripOptions = {}) {
    this.root = document.createElement('div');
    this.root.className = 'effect-strip';
    this.root.classList.toggle('effect-strip--tip-below', options.tipBelow === true);
    this.tip = document.createElement('div');
    this.tip.className = 'effect-strip__tip';
    this.tip.hidden = true;
    this.root.append(this.tip);
  }

  private openTip(status: StatusId | null): void {
    this.hovered = status;
    const entry = status === null ? undefined : this.shown.get(status);
    this.tip.hidden = entry === undefined;
    if (entry === undefined) return;
    // Centred over its own icon, inside the strip's row.
    this.tip.style.left = `${String(entry.element.offsetLeft + entry.element.offsetWidth / 2)}px`;
    this.tick(performance.now());
  }

  /** A state landed, or was renewed: shown, or its clock reset. */
  show(status: StatusId, durationMs: number): void {
    this.showUntil(status, performance.now() + durationMs, durationMs);
  }

  /** A state that ends at `endsAt` (performance.now clock) and lasts `durationMs` in all. */
  showUntil(status: StatusId, endsAt: number, durationMs: number): void {
    let entry = this.shown.get(status);
    if (entry === undefined) {
      const spell = spellFor(status);
      const element = document.createElement('div');
      element.className = 'effect-strip__effect';
      element.classList.toggle('effect-strip__effect--debuff', !isPlayerStatus(status));
      const text = describe(status, spell);
      element.setAttribute('aria-label', text);
      element.addEventListener('pointerenter', (event) => {
        if (event.pointerType === 'mouse') this.openTip(status);
      });
      element.addEventListener('pointerleave', (event) => {
        if (event.pointerType === 'mouse' && this.hovered === status) this.openTip(null);
      });
      // A touch screen has no hover: a tap opens the bubble, a second one closes it.
      element.addEventListener('click', () => {
        this.openTip(this.hovered === status ? null : status);
      });
      const icon = document.createElement('span');
      icon.className = 'effect-strip__icon';
      if (spell !== undefined) icon.style.backgroundImage = `url('/assets/ui/${spell.icon}.png')`;
      const sweep = document.createElement('span');
      sweep.className = 'effect-strip__sweep';
      const left = document.createElement('span');
      left.className = 'effect-strip__left';
      element.append(icon, sweep, left);
      this.root.append(element);
      entry = { element, sweep, left, endsAt: 0, durationMs: 0, text };
      this.shown.set(status, entry);
    }
    // A renewal never shortens a state (rules/statuses.ts): keep the later end.
    if (endsAt > entry.endsAt) {
      entry.endsAt = endsAt;
      entry.durationMs = durationMs;
    }
    this.tick(performance.now());
  }

  /** Death, leaving the zone or another target forgets every state shown. */
  clear(): void {
    for (const entry of this.shown.values()) entry.element.remove();
    this.shown.clear();
    this.openTip(null);
  }

  /** Called every frame by whoever holds the strip. */
  tick(now: number): void {
    for (const [status, entry] of this.shown) {
      const left = entry.endsAt - now;
      if (left <= 0) {
        entry.element.remove();
        this.shown.delete(status);
        if (this.hovered === status) this.openTip(null);
        continue;
      }
      const spent = entry.durationMs > 0 ? 1 - left / entry.durationMs : 1;
      entry.sweep.style.setProperty('--spent', `${String(Math.round(spent * 360))}deg`);
      const seconds = String(Math.ceil(left / 1000));
      const label = `${seconds}s`;
      if (entry.left.textContent !== label) entry.left.textContent = label;
      // The last two seconds blink, so its end does not come by surprise.
      entry.element.classList.toggle('effect-strip__effect--ending', left < 2000);
      if (this.hovered === status) {
        const remaining = uiFormat('Encore {seconds} s.', { seconds }).replace(/ /g, NBSP);
        const tip = `${entry.text} ${remaining}`;
        if (this.tip.textContent !== tip) this.tip.textContent = tip;
      }
    }
  }
}
